// ENDLESS GOALS save section (owner 2026-11-02): best endless time (s past the final boss) and best tier reached, overall
// and per map. An OPTIONAL profile field (Profile.endless): no save-version bump, absent on older saves and on profiles that
// never reached endless, and any shape loads (bad values are dropped). Pure functions (unit-tested).

export interface EndlessBest { time: number; tier: number }
export interface EndlessRecords { best: EndlessBest; maps: Record<string, EndlessBest> }

const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : 0);
const bestOf = (x: unknown): EndlessBest => {
  const r = x && typeof x === 'object' ? (x as Partial<EndlessBest>) : {};
  return { time: Math.floor(num(r.time)), tier: Math.floor(num(r.tier)) };
};

/** any saved value → records, or undefined when there is nothing worth keeping */
export function migrateEndless(raw: unknown): EndlessRecords | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<EndlessRecords>;
  const out: EndlessRecords = { best: bestOf(r.best), maps: {} };
  if (r.maps && typeof r.maps === 'object') for (const [k, v] of Object.entries(r.maps)) { const b = bestOf(v); if (b.time > 0 || b.tier > 0) out.maps[k] = b; } // unknown map ids kept
  // the overall best can never trail a map's best
  for (const b of Object.values(out.maps)) { out.best.time = Math.max(out.best.time, b.time); out.best.tier = Math.max(out.best.tier, b.tier); }
  return out.best.time > 0 || out.best.tier > 0 ? out : undefined;
}

export interface EndlessRecordResult { newTime: boolean; newTier: boolean; newMapTime: boolean; newMapTier: boolean }
const NONE: EndlessRecordResult = { newTime: false, newTier: false, newMapTime: false, newMapTier: false };

/** An endless run ended (death or quit): keep the bests. `time` = endless seconds, `tier` = endless tier reached.
 *  Training runs (and runs that never went endless) record nothing. */
export function recordEndless(p: { endless?: EndlessRecords }, mapId: string, time: number, tier: number, training = false): EndlessRecordResult {
  const t = Math.floor(num(time)), n = Math.floor(num(tier));
  if (training || (t <= 0 && n <= 0)) return { ...NONE };
  const e = p.endless ?? (p.endless = { best: { time: 0, tier: 0 }, maps: {} });
  const m = e.maps[mapId] ?? (e.maps[mapId] = { time: 0, tier: 0 });
  const res: EndlessRecordResult = { newTime: t > e.best.time, newTier: n > e.best.tier, newMapTime: t > m.time, newMapTier: n > m.tier };
  if (res.newTime) e.best.time = t; if (res.newTier) e.best.tier = n;
  if (res.newMapTime) m.time = t; if (res.newMapTier) m.tier = n;
  return res;
}

/** a map's endless bests (null = never reached endless there) */
export function endlessBestFor(p: { endless?: EndlessRecords }, mapId: string): EndlessBest | null {
  const m = p.endless?.maps[mapId]; return m && (m.time > 0 || m.tier > 0) ? m : null;
}

/** is this a training run? (the training-mode work adds a `training` flag; any of its likely homes counts) */
export function isTrainingRun(sim: unknown): boolean {
  const s = sim as { training?: unknown; opts?: { training?: unknown } } | null;
  return !!(s && (s.training || s.opts?.training || (s.opts as { calm?: unknown } | undefined)?.calm)); // (+ the tutorial's Basics lesson)
}
